import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Toolbar as MuiToolbar } from "@mui/material";
import PetsTable from "../components/PetsTable";
import ServicesTable from "../components/ServicesTable";
import Pagination from "../components/Pagination";
import { useServicesState } from "../hooks/useServicesState";
import { ACTIONS } from "../reducers/listActions";
import CustomerInfoPanel from "../components/CustomerInfoPanel";
import { usePetState } from "../hooks/usePetState";
import { useRefresh } from "../context/RefreshContext";
import api from "../services/api";

const customerPetsColumns = [
  { id: "redirect", label: "", sortable: false },
  { id: "name", label: "Name", sortable: true },
  { id: "breed", label: "Breed", sortable: true },
  { id: "sex", label: "Sex", sortable: true },
  { id: "weight", label: "Weight", sortable: true },
  { id: "color", label: "Color", sortable: true },
  { id: "age", label: "Age", sortable: true },
  { id: "medicalIssues", label: "Medical Issues", sortable: false },
  { id: "comments", label: "", sortable: false },
  { id: "imageUrl", label: "", sortable: false },
];

const customerServicesColumns = [
  { id: "redirect", label: "", sortable: false },
  { id: "date", label: "Date", sortable: true },
  { id: "time", label: "Time", sortable: false },
  { id: "service", label: "Service", sortable: false },
  { id: "amount", label: "Amount", sortable: false },
  { id: "pet", label: "Pet", sortable: false },
  { id: "groomer", label: "Groomer", sortable: false },
  { id: "comments", label: "", sortable: false },
  { id: "imageUrl", label: "", sortable: false },
];

const CustomerDetails = () => {
  const { id } = useParams();
  const { refreshCounter } = useRefresh();

  const { state: petState, dispatch: petDispatch, setPage: setPetsPage, setRowsPerPage: setPetsRowsPerPage, handleSortRequest: handlePetsSort } = usePetState();
  const { pets: petsData, order: petsOrder, orderBy: petsOrderBy, page: petsPage, rowsPerPage: petsRowsPerPage, totalPets } = petState;

  const { state, dispatch, setPage, setRowsPerPage, handleSortRequest } = useServicesState();
  const { services, order, orderBy, page, rowsPerPage, totalServices } = state;

  useEffect(() => {
      async function fetchCustomerPets() {
        try {
          const token = localStorage.getItem("token");
          const response = await api.get(`/customers/${id}/pets`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              page: petsPage + 1, // assuming backend pages are 1-indexed
              limit: petsRowsPerPage,
              sortBy: petState.orderBy,
              order: petState.order,
            },
          });
          petDispatch({
            type: ACTIONS.FETCH_SUCCESS,
            payload: {
              pets: response.data.data,
              total: response.data.total,
            },
          });
        } catch (error) {
          petDispatch({
            type: ACTIONS.FETCH_FAILURE,
            payload: "Failed to fetch pets.",
          });
        }
      }
      fetchCustomerPets();
    }, [id, petsPage, petsRowsPerPage, petState.orderBy, petState.order, petDispatch, refreshCounter]);
  
    useEffect(() => {
      async function fetchCustomerServices() {
        try {
          const token = localStorage.getItem("token");
          const response = await api.get(`/customers/${id}/services`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              page: page + 1,
              limit: rowsPerPage,
              sortBy: orderBy,
              order: order,
            },
          });
          dispatch({
            type: ACTIONS.FETCH_SUCCESS,
            payload: {
              services: response.data.data,
              total: response.data.total,
            },
          });
        } catch (error) {
          dispatch({ type: ACTIONS.FETCH_FAILURE, payload: "Failed to fetch services." });
        }
      }
      fetchCustomerServices();
    }, [id, page, rowsPerPage, order, orderBy, dispatch, refreshCounter]);

  return (
    <>
      <MuiToolbar />
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Move all state & logic to CustomerInfoPanel */}
        <CustomerInfoPanel customerId={id} />

        {/* Pets Table */}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Pets</Typography>
          {petsData.length > 0 ? (
            <>
              {totalPets > petsRowsPerPage && (
                <Pagination
                  count={totalPets}
                  page={petsPage}
                  rowsPerPage={petsRowsPerPage}
                  onPageChange={(e, newPage) => setPetsPage(newPage)}
                  onRowsPerPageChange={(e) => setPetsRowsPerPage(parseInt(e.target.value, 10))}
                />
              )}
              <PetsTable
                data={petsData}
                columns={customerPetsColumns}
                order={petsOrder}
                orderBy={petsOrderBy}
                onSortRequest={handlePetsSort}
              />
              {totalPets > petsRowsPerPage && (
                <Pagination
                  count={totalPets}
                  page={petsPage}
                  rowsPerPage={petsRowsPerPage}
                  onPageChange={(e, newPage) => setPetsPage(newPage)}
                  onRowsPerPageChange={(e) => setPetsRowsPerPage(parseInt(e.target.value, 10))}
                />
              )}
            </>
          ) : (
            <Typography>No pets found.</Typography>
          )}
        </Box>

        {/* Services Table */}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Services</Typography>
          {totalServices > 0 ? (
            <>
              {totalServices > rowsPerPage && (
                <Pagination
                  count={totalServices}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
              )}
              <ServicesTable
                data={services}
                columns={customerServicesColumns}
                order={order}
                orderBy={orderBy}
                onSortRequest={handleSortRequest}
              />
              {totalServices > rowsPerPage && (
                <Pagination
                  count={totalServices}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
              )}
            </>
          ) : (
            <Typography>No services found.</Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CustomerDetails;